import { CITY_TIERS } from "@/lib/constants";

// Years of expense each bucket aims to cover (used for refill cascade).
const COVERAGE_YEARS = [3, 7, 12, 18];

// Full year-by-year retirement SWP simulation.
// opts.stress = { years, ret } applies a bad-market run (ret % annual) for the
// first `years` of retirement to model sequence-of-returns risk.
export function computePlan(input, opts = {}) {
  const {
    currentAge, retirementAge, longevityAge, currentCorpus, monthlyExpenses,
    annualExtra, inflation, cityTier, strategy, swpReturn, buckets,
    monthlyContribution = 0, contributionStepUp = 0,
    incomeSources = [], withdrawalMode = "gap", swpRate = 4,
    inflationStartYear = 1,
  } = input;

  const stress = opts.stress || null;

  const infl = (inflation || 0) / 100;
  const tier = CITY_TIERS.find((t) => t.id === cityTier) || CITY_TIERS[1];
  const tierMult = tier.mult;

  const yearsToRet = Math.max(0, retirementAge - currentAge);
  const retirementYears = Math.max(1, longevityAge - retirementAge);

  const preReturn = strategy === "bucket"
    ? buckets.reduce((s, b) => s + (b.allocation / 100) * (b.return / 100), 0)
    : (swpReturn || 0) / 100;

  // Accumulation phase: grow current corpus + annual contributions (with step-up).
  // Contributions use a mid-year convention (1 + preReturn/2) as a simple approximation.
  let accumulated = currentCorpus;
  let annualContribution = monthlyContribution * 12;
  const step = (contributionStepUp || 0) / 100;
  let totalContributed = 0;
  for (let y = 0; y < yearsToRet; y++) {
    accumulated = accumulated * (1 + preReturn) + annualContribution * (1 + preReturn / 2);
    totalContributed += annualContribution;
    annualContribution *= 1 + step;
  }
  const retirementCorpus = accumulated;

  const baseAnnual = monthlyExpenses * 12 * tierMult;
  const firstYearExpense =
    baseAnnual * Math.pow(1 + infl, yearsToRet) + annualExtra * Math.pow(1 + infl, yearsToRet);

  // Annual income from all non-corpus sources at a given retirement age.
  const otherIncomeAt = (age) =>
    (incomeSources || []).reduce((sum, s) => {
      const start = s.startAge ?? retirementAge;
      const end = s.endAge ?? longevityAge;
      if (age < start || age > end) return sum;
      const grown = (s.monthly || 0) * Math.pow(1 + (s.growth || 0) / 100, Math.max(0, age - start));
      return sum + grown * 12;
    }, 0);

  const projection = [];
  let depletionAge = null;

  const yearReturn = (baseR, idx) =>
    stress && idx < stress.years ? stress.ret / 100 : baseR;

  // Inflation ramp can commence from year N (years before N stay at today's level).
  const inflFactor = (idx) => Math.pow(1 + infl, Math.max(0, idx - (inflationStartYear - 1)));

  // Corpus withdrawal target for a year: gap-funding vs fixed-% SWP.
  const targetFor = (expense, otherIncome, openCorpus) =>
    withdrawalMode === "fixed"
      ? Math.max(0, (swpRate / 100) * openCorpus)
      : Math.max(0, expense - otherIncome);

  if (strategy === "swp") {
    let corpus = retirementCorpus;
    for (let idx = 0; idx < retirementYears; idx++) {
      const age = retirementAge + idx;
      const expense = firstYearExpense * inflFactor(idx);
      const otherIncome = otherIncomeAt(age);
      const open = corpus;
      const target = targetFor(expense, otherIncome, open);
      const withdrawal = Math.min(target, Math.max(0, open));
      if (withdrawalMode === "gap" && target - open > 0.5 && depletionAge === null) depletionAge = age;
      if (withdrawalMode === "fixed" && open <= 0.5 && depletionAge === null) depletionAge = age;
      const afterWithdraw = Math.max(0, open - withdrawal);
      const growth = afterWithdraw * yearReturn((swpReturn || 0) / 100, idx);
      corpus = afterWithdraw + growth;
      const totalIncome = otherIncome + withdrawal;
      projection.push({ age, year: idx + 1, open, withdrawal, growth, close: corpus,
        expense, otherIncome, totalIncome, gap: expense - totalIncome });
    }
  } else {
    const bals = buckets.map((b) => retirementCorpus * (b.allocation / 100));
    const baseRets = buckets.map((b) => b.return / 100);
    const n = bals.length;

    for (let idx = 0; idx < retirementYears; idx++) {
      const age = retirementAge + idx;
      const expense = firstYearExpense * inflFactor(idx);
      const otherIncome = otherIncomeAt(age);
      const open = bals.reduce((s, v) => s + v, 0);
      const target = targetFor(expense, otherIncome, open);

      // Cascade withdrawal: first bucket -> last bucket.
      let need = target;
      for (let i = 0; i < n; i++) {
        const t = Math.min(bals[i], need);
        bals[i] -= t;
        need -= t;
      }
      const withdrawal = target - need;
      if (withdrawalMode === "gap" && need > 0.5 && depletionAge === null) depletionAge = age;
      if (withdrawalMode === "fixed" && open <= 0.5 && depletionAge === null) depletionAge = age;

      // Grow each bucket (stress applied in early years).
      for (let i = 0; i < n; i++) bals[i] *= 1 + yearReturn(baseRets[i], idx);
      const afterGrow = bals.reduce((s, v) => s + v, 0);
      const growth = afterGrow - (open - withdrawal);

      // Refill: keep each bucket topped to its coverage target, pulling from the next.
      const nextTarget = withdrawalMode === "fixed"
        ? (swpRate / 100) * afterGrow
        : Math.max(0, firstYearExpense * inflFactor(idx + 1) - otherIncomeAt(age + 1));
      for (let i = 0; i < n - 1; i++) {
        const target2 = nextTarget * (COVERAGE_YEARS[i] || 12);
        if (bals[i] < target2) {
          const m = Math.min(bals[i + 1], target2 - bals[i]);
          bals[i] += m;
          bals[i + 1] -= m;
        }
      }

      const close = bals.reduce((s, v) => s + v, 0);
      const totalIncome = otherIncome + withdrawal;
      const row = { age, year: idx + 1, open, withdrawal, growth, close,
        expense, otherIncome, totalIncome, gap: expense - totalIncome };
      row.b1 = bals[0];
      row.b2 = bals[1];
      row.b3 = bals[2];
      if (n >= 4) row.b4 = bals[3];
      projection.push(row);
    }
  }

  const finalCorpus = projection.length ? projection[projection.length - 1].close : retirementCorpus;
  const totalWithdrawn = projection.reduce((s, p) => s + p.withdrawal, 0);
  const first = projection[0] || {};
  const swr = retirementCorpus > 0 ? ((first.withdrawal || 0) / retirementCorpus) * 100 : 0;

  let status;
  if (depletionAge !== null) status = "depleted";
  else if (finalCorpus > retirementCorpus * 0.25) status = "surplus";
  else status = "warning";

  const yearsLasted = depletionAge !== null ? depletionAge - retirementAge : retirementYears;

  const firstAge = first.age ?? retirementAge;
  const incomeBreakdown = [
    { label: strategy === "bucket" ? "Corpus (Bucket SWP)" : "Corpus (SWP)", monthly: Math.round((first.withdrawal || 0) / 12), key: "swp" },
    ...(incomeSources || [])
      .filter((s) => firstAge >= (s.startAge ?? retirementAge) && firstAge <= (s.endAge ?? longevityAge))
      .map((s) => ({ label: s.label || s.type, monthly: Math.round(s.monthly || 0), key: s.id || s.label })),
  ].filter((x) => x.monthly > 0);

  return {
    retirementCorpus, firstYearExpense, swr, projection, depletionAge,
    finalCorpus, totalWithdrawn, status, retirementYears, yearsToRet,
    yearsLasted, totalContributed, preReturn: preReturn * 100,
    incomeBreakdown, withdrawalMode,
    firstYearMonthlyIncome: Math.round((first.totalIncome || 0) / 12),
    firstYearMonthlyExpense: Math.round((first.expense || firstYearExpense) / 12),
    firstYearOtherIncome: Math.round(first.otherIncome || 0),
  };
}


// Builds a full-period cashflow schedule (accumulation + retirement) that
// reconciles exactly to computePlan's yearly numbers, plus a month-by-month
// expansion (contribution / withdrawal / growth split evenly across 12 months).
export function buildSchedule(input, result) {
  const {
    currentAge, retirementAge, currentCorpus,
    monthlyContribution = 0, contributionStepUp = 0,
    strategy, swpReturn, buckets,
  } = input;

  const preReturn = strategy === "bucket"
    ? buckets.reduce((s, b) => s + (b.allocation / 100) * (b.return / 100), 0)
    : (swpReturn || 0) / 100;
  const step = (contributionStepUp || 0) / 100;
  const startYear = new Date().getFullYear();
  const yearsToRet = Math.max(0, retirementAge - currentAge);

  const annual = [];

  // Accumulation phase
  let bal = currentCorpus;
  let annualContribution = monthlyContribution * 12;
  for (let y = 0; y < yearsToRet; y++) {
    const open = bal;
    const contribution = annualContribution;
    const close = open * (1 + preReturn) + contribution * (1 + preReturn / 2);
    const growth = close - open - contribution;
    annual.push({
      phase: "accumulation", age: currentAge + y, calYear: startYear + y,
      open, contribution, withdrawal: 0, growth, close,
    });
    bal = close;
    annualContribution *= 1 + step;
  }

  // Retirement phase (straight from the reconciled yearly projection)
  result.projection.forEach((p, i) => {
    annual.push({
      phase: "retirement", age: p.age, calYear: startYear + yearsToRet + i,
      open: p.open, contribution: 0, withdrawal: p.withdrawal, growth: p.growth, close: p.close,
    });
  });

  // Monthly expansion
  const monthly = [];
  annual.forEach((row) => {
    let mbal = row.open;
    const mc = row.contribution / 12;
    const mw = row.withdrawal / 12;
    const mg = row.growth / 12;
    for (let m = 0; m < 12; m++) {
      const open = mbal;
      const close = open + mc - mw + mg;
      monthly.push({
        phase: row.phase, age: row.age, calYear: row.calYear, monthIndex: m,
        open, contribution: mc, withdrawal: mw, growth: mg, close,
      });
      mbal = close;
    }
  });

  return { annual, monthly, startYear };
}
